import React, { useState, useMemo } from 'react';
import styles from './create-booking-modal.module.scss';
import { ModalWindow, DefaultButton } from 'shared/ui';

interface ICreateBookingData {
  startTime: string;
  employeeId: string;
  date: Date;
}

interface ICreateBookingModalProps {
  startTime: string;
  duration: number;
  employeeId: string;
  date: Date;
  onClose: () => void;
  onCreateBooking?: (data: ICreateBookingData) => void;
  onCreateBreak?: (data: ICreateBookingData) => void;
}

type BookingType = 'booking' | 'break';

export const CreateBookingModal: React.FC<ICreateBookingModalProps> = ({
  startTime,
  duration,
  employeeId,
  date,
  onClose,
  onCreateBooking,
  onCreateBreak,
}) => {
  const [selectedType, setSelectedType] = useState<BookingType>('booking');

  const timeOptions = useMemo(() => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const baseMinutes = hours * 60 + minutes;
    const times: string[] = [];

    for (let i = -1; i <= 2; i++) {
      const totalMinutes = baseMinutes + i * duration;

      // Clamp negative times to 00:00 for now (visual mode)
      const clampedMinutes = Math.max(0, totalMinutes);

      const h = Math.floor(clampedMinutes / 60);
      const m = clampedMinutes % 60;
      times.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }

    return times;
  }, [startTime, duration]);

  const [selectedTime, setSelectedTime] = useState<string>(
    timeOptions[1] || timeOptions[0] || startTime
  );

  const handleCancel = () => {
    onClose();
  };

  const handleCreate = () => {
    const data = { startTime: selectedTime, employeeId, date };
    if (selectedType === 'booking' && onCreateBooking) {
      onCreateBooking(data);
    } else if (selectedType === 'break' && onCreateBreak) {
      onCreateBreak(data);
    }
    onClose();
  };

  return (
    <ModalWindow onClickOutside={onClose}>
      <div className={styles.modalContent}>
        <div className={styles.typeSelector}>
          <DefaultButton
            buttonText="Запись"
            onClick={() => setSelectedType('booking')}
            color={selectedType === 'booking' ? 'green' : 'white'}
          />
          <DefaultButton
            buttonText="Перерыв"
            onClick={() => setSelectedType('break')}
            color={selectedType === 'break' ? 'green' : 'white'}
          />
        </div>

        <div className={styles.timePickerContainer}>
          {timeOptions.map((time) => (
            <button
              key={time}
              onClick={() => setSelectedTime(time)}
              className={`${styles.timeOption} ${
                time === selectedTime ? styles.timeOptionActive : ''
              }`}
            >
              {time}
            </button>
          ))}
        </div>

        <div className={styles.actionButtons}>
          <DefaultButton
            buttonText="Отменить"
            onClick={handleCancel}
            color="red"
          />
          <DefaultButton
            buttonText="Создать"
            onClick={handleCreate}
            color="green"
          />
        </div>
      </div>
    </ModalWindow>
  );
};
